import React from "react";
import { Action, Dispatch } from "redux";
import { getFormattedDate } from "../../../Versioning/GetVersions/helper";
import {
  TableHeaderProps,
  TableProps,
  TableDataRowProps,
  Badge
} from "../../../../Common/UI/Index";
import { OutputConfigSlice } from "../../redux";
import { INVALID, VALID } from "../../Constant";
import { BadgeVariant } from "../../../../Common/UI/Types";
import { OutputConfigRemove } from "./RemoveOutputConfig.Logic";
import { DeleteTypes } from "./Constants";
import Edit from "~icons/ic/outline-edit";
import "./Style.scss";

export interface ItemProps {
  /**
   * ID of the config
   */
  outputConfigGuid: string;
  /**
   * Name of the config
   */
  name: string;
  /**
   * Config created by
   */
  createdBy: string;
  /**
   * Config created on
   */
  createdOn: string;
  /**
   * Config modified by
   */
  modifiedBy: string;
  /**
   * Config modified on
   */
  modifiedOn: string;
  /**
   * Name of the file
   */
  xsdFileName: string;
  /**
   * Version of the extract
   */
  extractVersion: string;
  /**
   * Config is valid or not
   */
  isValid: boolean;
}

/**
* Method to handle styling with respect to isValid of the output config
* @param statusType Config status
* @returns JSX element
*/
const getStatus = (statusType: boolean): JSX.Element => {
  const formattedStatus = statusType ? VALID : INVALID;
  return <div className={`ebt-config-isvalid__${formattedStatus}`}>{formattedStatus}</div>;
};

/**
 * To render the action column icons
 * @returns the icons
 * NOTE: Type any is used because UI Table accepts only strings as values
 * but we need icons in the table row
 */
const getActionColumn = (item: ItemProps, dispatch: Dispatch<Action>): any => {
  /**
   * Handler for the output configuration edit
   */
  const handleEditClick = (): void => {
    dispatch(OutputConfigSlice.actions.setSelectedConfig({
      id: item.outputConfigGuid,
      name: item.name,
      fileName: item.xsdFileName
    }));
  };

  return (
    <div className="ebt-output-config__list__actions">
      <div
        onClick={handleEditClick}
      >
        <Edit
          title="edit"
          className="ebt-output-config__list__edit-icon"
        />
      </div>
      <div className="ebt-output-config__list__delete-icon">
        <OutputConfigRemove deleteType={DeleteTypes.DELETE_OUTPUT_LIST} item={item} />
      </div>
    </div>
  );
};

/**
 * To bind the table data
 * @param data response data
 * @returns the formatted table row data
 */
const buildConfigListTableRows = (data: string, dispatch: Dispatch<Action>): TableDataRowProps[] => {
  const response = JSON.parse(data);
  let outputData: TableDataRowProps[] = [];
  const inputData = response && response.payload && response.payload.outputConfig;
  outputData = inputData.map((item: ItemProps) => {
    const output: TableDataRowProps = {
      row: [
        {
          value: item.name,
          propertyName: "name"
        },
        {
          value: item.extractVersion ?
            <Badge
              className="ebt-output-config__list-version-badge"
              value={item.extractVersion}
              variant={BadgeVariant.Primary}
            /> :
            "-",
          propertyName: "version"
        },
        {
          value: item.createdBy,
          propertyName: "Createdby"
        },
        {
          value: getFormattedDate(new Date(item.createdOn), true),
          propertyName: "Createdon"
        },
        {
          value: item.modifiedBy || "-",
          propertyName: "Modifiedby"
        },
        {
          value: item.modifiedOn ? getFormattedDate(new Date(item.modifiedOn), true) : "-",
          propertyName: "Modifiedon"
        },
        {
          value: getStatus(item.isValid) || "-",
          propertyName: "status"
        },
        {
          value: getActionColumn(item, dispatch),
          propertyName: "actions"
        }
      ]
    };
    return output;
  });

  return outputData;
};

/**
 * To populate the table data
 * @param data response data
 * @param dispatch function to dispatch
 * @param isUnMount Flag for unmount
 * @returns the formatted data to bind the table
 */
export const PopulateConfigListTable = (
  data: string,
  dispatch: Dispatch<Action>,
  isUnMount?: boolean
): TableProps => {
  let table: TableProps = {} as TableProps;
  const tableHeader: TableHeaderProps[] = [
    {
      title: "Output config name",
      propertyName: "name"
    },
    {
      title: "Version",
      propertyName: "version"
    },
    {
      title: "Created by",
      propertyName: "Createdby"
    },
    {
      title: "Created on",
      propertyName: "Createdon"
    },
    {
      title: "Modified by",
      propertyName: "Modifiedby"
    },
    {
      title: "Modified on",
      propertyName: "Modifiedon"
    },
    {
      title: "Status",
      propertyName: "status"
    },
    {
      title: "Actions",
      propertyName: "actions"
    }
  ];

  table = {
    headerRow: tableHeader,
    dataRow: isUnMount ? [] : buildConfigListTableRows(data, dispatch)
  };

  return table;
};
